// @flow

import * as React from "react";
import Helmet from "react-helmet";
import { useSelector } from "react-redux";
import * as decode from "parse-entities";
import usePageFromStore from "../../hooks/usePageFromStore";
import { getFeaturedImage } from "../../utils/post";
import Config from "../../constants/Config";
import { cloneDeep } from "lodash";

const YoastMeta = (): React.Node => {
    const { page, loading } = usePageFromStore();

    const currentPagePath = useSelector(
        (state: any): any => state.page.currentPagePath
    );

    React.useEffect((): any => {
        // Do nothing - just force a render whenever one of the below properties change
    }, [page, loading, currentPagePath]);

    if (!page && loading) return null;

    if (page.seo) {
        return <YoastMetaSeo {...page} />;
    } else if (page.yoast_head_json) {
        return <YoastMetaJSON {...page} />;
    } else {
        return null;
    }
};

const YoastMetaJSON = props => {
    const image =
        props.yoast_head_json.og_image && props.yoast_head_json.og_image[0]
            ? props.yoast_head_json.og_image[0]
            : null;

    const pageTitle = props.yoast_head_json.og_title
        ? props.yoast_head_json.og_title
        : `${props.post_title || props.title}${Config.siteNameSeperator}${
              Config.siteName
          }`;

    return (
        <Helmet>
            <title>{pageTitle}</title>

            <link rel="canonical" href={props.yoast_head_json.canonical} />
            <meta
                property="og:locale"
                content={props.yoast_head_json.og_locale}
            />
            <meta property="og:type" content={props.yoast_head_json.og_type} />
            <meta
                property="og:title"
                content={props.yoast_head_json.og_title}
            />
            <meta
                property="og:description"
                content={props.yoast_head_json.og_description}
            />
            <meta property="og:url" content={props.yoast_head_json.og_url} />
            <meta
                property="og:site_name"
                content={props.yoast_head_json.og_site_name}
            />
            <meta
                property="article:modified_time"
                content={props.yoast_head_json.article_modified_time}
            />
            {image ? <meta property="og:image" content={image.url} /> : null}
            {image ? (
                <meta property="og:image:width" content={image.width} />
            ) : null}
            {image ? (
                <meta property="og:image:height" content={image.height} />
            ) : null}

            <meta
                name="twitter:card"
                content={props.yoast_head_json.twitter_card}
            />
        </Helmet>
    );
};

const YoastMetaSeo = props => {
    const hasFeaturedImage = !!props.seo.meta.find((item): any => {
        return item.property === "og:image";
    });
    const seoMeta = cloneDeep(props.seo.meta);

    if (!hasFeaturedImage) {
        const featuredImg = getFeaturedImage(props);
        if (featuredImg) {
            seoMeta.push({
                property: "og:image",
                content: featuredImg.url,
            });
            seoMeta.push({
                property: "og:image:width",
                content: featuredImg.width.toString(),
            });
            seoMeta.push({
                property: "og:image:height",
                content: featuredImg.height.toString(),
            });
        }
    }

    seoMeta.map((tags: Object, index: number): any => {
        for (const [key, value] of Object.entries(tags)) {
            if (value) {
                tags[key] = decode(value);
            }
        }
        return null;
    });

    const titleField = seoMeta.find(metaTag => {
        return metaTag.property === "og:title";
    });

    const pageTitle = titleField
        ? titleField.content
        : `${props.post_title || props.title}${Config.siteNameSeperator}${
              Config.siteName
          }`;

    return (
        <Helmet>
            <title>{pageTitle}</title>

            {seoMeta.map((tag: any, index: number): React.Node => (
                <meta key={index} {...tag} />
            ))}
        </Helmet>
    );
};

export default YoastMeta;
